import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import {
  Code2,
  Gamepad2,
  ShieldCheck,
  BarChart3,
  Palette,
  Cloud,
  Mail,
  Phone,
  MapPin,
  GraduationCap,
  Briefcase,
  Globe,
  Clock,
  CheckCircle,
  Building2,
  BookOpen,
  Swords,
  Wrench,
  Globe2,
  Microscope,
  ExternalLink,
} from 'lucide-react';
import { BentoCell } from './BentoCell';
import { SkeletonLoader } from './SkeletonLoader';
import { content } from '../../data/content';
import { variantContent } from '../../data/variants';
import type { Lang, Variant } from '../../App';

type CopiedField = 'email' | 'phone' | null;

const SKILL_ICON_MAP: Record<string, React.ReactNode> = {
  code: <Code2 size={16} />,
  game: <Gamepad2 size={16} />,
  shield: <ShieldCheck size={16} />,
  reverse: <ShieldCheck size={16} />,
  data: <BarChart3 size={16} />,
  art: <Palette size={16} />,
  devops: <Cloud size={16} />,
};

const PROJECT_ICON_MAP: Record<string, React.ReactNode> = {
  building: <Building2 size={20} />,
  book: <BookOpen size={20} />,
  sword: <Swords size={20} />,
  wrench: <Wrench size={20} />,
  globe: <Globe2 size={20} />,
  microscope: <Microscope size={20} />,
};

const PROJECT_GRADIENTS = [
  'linear-gradient(135deg, rgba(59,130,246,0.35), rgba(139,92,246,0.2))',
  'linear-gradient(135deg, rgba(34,197,94,0.3), rgba(6,182,212,0.15))',
  'linear-gradient(135deg, rgba(239,68,68,0.3), rgba(249,115,22,0.15))',
  'linear-gradient(135deg, rgba(139,92,246,0.35), rgba(236,72,153,0.15))',
  'linear-gradient(135deg, rgba(6,182,212,0.3), rgba(59,130,246,0.15))',
  'linear-gradient(135deg, rgba(249,115,22,0.3), rgba(234,179,8,0.15))',
];

function useAnimatedCounter(target: number, duration: number = 1500, start: boolean = true): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    let raf: number;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return count;
}

function PhotoCell({ lang }: { lang: Lang }) {
  return (
    <div className="bento-photo-cell">
      <img
        src="/photo.jpg"
        alt="Jordan Rivera Rodriguez"
        className="bento-photo-img"
      />
      <div className="bento-photo-overlay">
        <p className="bento-photo-greeting">
          {lang === 'en' ? "Hi, I'm" : 'Hola, soy'}
        </p>
        <h1 className="bento-photo-name">Jordan Rivera</h1>
      </div>
    </div>
  );
}

function TitlesCell({ lang, variant }: { lang: Lang; variant: Variant }) {
  const titles = variantContent[lang][variant].titles;
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
    const i = setInterval(() => setIdx(n => (n + 1) % titles.length), 2500);
    return () => clearInterval(i);
  }, [titles]);

  return (
    <div className="bento-titles-cell">
      <span className="bento-titles-label">
        {lang === 'en' ? 'CURRENTLY' : 'ACTUALMENTE'}
      </span>
      <div className="bento-titles-wrapper">
        <AnimatePresence mode="wait">
          <motion.h2
            key={`${variant}-${idx}`}
            className="bento-titles-text"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.35 }}
          >
            {titles[idx]}
          </motion.h2>
        </AnimatePresence>
      </div>
      <div className="bento-titles-dots">
        {titles.map((_, i) => (
          <span key={i} className={`bento-dot ${i === idx ? 'active' : ''}`} />
        ))}
      </div>
    </div>
  );
}

function StatsCell({ lang, loaded }: { lang: Lang; loaded: boolean }) {
  const t = content[lang].about;
  const targets = [8, 50, 15, 2];
  const suffixes = ['+', '+', '+', ''];
  const c0 = useAnimatedCounter(targets[0], 1200, loaded);
  const c1 = useAnimatedCounter(targets[1], 1600, loaded);
  const c2 = useAnimatedCounter(targets[2], 1400, loaded);
  const c3 = useAnimatedCounter(targets[3], 800, loaded);
  const counts = [c0, c1, c2, c3];

  return (
    <div className="bento-stats-cell">
      {t.stats.map((s, i) => (
        <div key={i} className="bento-stat">
          <span className="bento-stat-value">
            {counts[i]}{suffixes[i]}
          </span>
          <span className="bento-stat-label">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

function StatusCell({ lang }: { lang: Lang }) {
  return (
    <div className="bento-status-cell">
      <span className="bento-status-dot-lg" />
      <span className="bento-status-label">
        {lang === 'en' ? 'Available for Work' : 'Disponible'}
      </span>
      <span className="bento-status-tz-badge">PST/PDT</span>
    </div>
  );
}

function LocationCell({ lang }: { lang: Lang }) {
  return (
    <div className="bento-location-cell">
      <MapPin size={28} style={{ color: 'rgba(59,130,246,0.7)' }} />
      <p className="bento-location-city">Mexicali, MX</p>
      <p className="bento-location-us">
        {lang === 'en' ? 'US Meetings Available' : 'Reuniones en EE.UU.'}
      </p>
    </div>
  );
}

function JobCell({
  lang,
  jobIndex,
  isCurrent,
}: {
  lang: Lang;
  jobIndex: number;
  isCurrent: boolean;
}) {
  const job = content[lang].experience.jobs[jobIndex];
  if (!job) return null;

  const periodParts = job.period.split('\u2013');
  const periodClean = periodParts.length > 1
    ? `${periodParts[0].trim()} - ${periodParts[1].trim()}`
    : job.period;

  return (
    <div className={`bento-job-cell ${isCurrent ? 'current' : ''}`}>
      <div className="bento-job-header">
        <Briefcase size={14} style={{ opacity: 0.5, flexShrink: 0 }} />
        <span className="bento-job-title">{job.title}</span>
      </div>
      <div className="bento-job-company">{job.company} · {job.location}</div>
      <div className="bento-job-period">
        {isCurrent && (
          <span className="bento-job-current-badge">
            {lang === 'en' ? 'NOW' : 'HOY'}
          </span>
        )}{' '}
        <span>{periodClean}</span>
      </div>
      <ul className="bento-job-points">
        {job.points.slice(0, isCurrent ? 3 : 2).map((p, j) => (
          <li key={j}>
            <CheckCircle size={12} style={{ flexShrink: 0, marginTop: 3, opacity: 0.6 }} />
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SkillHighlightCell({ lang, variant }: { lang: Lang; variant: Variant }) {
  const highlights = variantContent[lang][variant].skillCategories.slice(0, 4);
  return (
    <div className="bento-skill-highlight-cell">
      <span className="bento-skill-hl-label">
        {lang === 'en' ? 'CORE EXPERTISE' : 'ESPECIALIDADES'}
      </span>
      {highlights.map((cat, i) => (
        <div key={i} className="bento-skill-hl-row">
          <span className="bento-skill-hl-icon" style={{ color: cat.color }}>
            {SKILL_ICON_MAP[cat.icon] || <Code2 size={16} />}
          </span>
          <div className="bento-skill-hl-info">
            <span className="bento-skill-hl-name">{cat.name}</span>
            <span className="bento-skill-hl-count">
              {cat.items.slice(0, 4).join(' · ')}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function ProjectCell({
  lang,
  variant,
  index,
  featured,
}: {
  lang: Lang;
  variant: Variant;
  index: number;
  featured?: boolean;
}) {
  const p = variantContent[lang][variant].projects[index];
  if (!p) return null;

  return (
    <div
      className={`bento-project-cell ${featured ? 'featured' : ''}`}
    >
      <span className="bento-project-icon">{PROJECT_ICON_MAP[p.icon] || <Building2 size={20} />}</span>
      <h3 className="bento-project-name">{p.name}</h3>
      <p className="bento-project-desc">{p.description}</p>
      <div className="bento-project-tags">
        {p.tech.map((t, i) => (
          <span key={i} className="bento-project-tag">{t}</span>
        ))}
      </div>
      {p.href && (
        <a href={p.href} target="_blank" rel="noopener noreferrer" className="bento-project-link">
          <ExternalLink size={12} /> {lang === 'en' ? 'Live Demo' : 'Ver en Vivo'}
        </a>
      )}
    </div>
  );
}

function SkillsTechCell({ lang, variant }: { lang: Lang; variant: Variant }) {
  const v = variantContent[lang][variant];
  return (
    <div className="bento-cloud-section">
      <span className="bento-cloud-section-label">{v.cloudTechLabel}</span>
      <div className="bento-cloud-section-tags">
        {v.cloudTech.map((item) => (
          <span key={item} className="bento-cloud-tag" style={{ borderColor: '#3b82f633', background: '#3b82f615', color: '#3b82f6' }}>{item}</span>
        ))}
      </div>
    </div>
  );
}

function SkillsToolsCell({ lang, variant }: { lang: Lang; variant: Variant }) {
  const v = variantContent[lang][variant];
  return (
    <div className="bento-cloud-section">
      <span className="bento-cloud-section-label">{v.cloudToolsLabel}</span>
      <div className="bento-cloud-section-tags">
        {v.cloudTools.map((item) => (
          <span key={item} className="bento-cloud-tag" style={{ borderColor: '#22c55e33', background: '#22c55e15', color: '#22c55e' }}>{item}</span>
        ))}
      </div>
    </div>
  );
}

function SkillsPracticesCell({ lang, variant }: { lang: Lang; variant: Variant }) {
  const v = variantContent[lang][variant];
  return (
    <div className="bento-cloud-section">
      <span className="bento-cloud-section-label">{v.cloudPracticesLabel}</span>
      <div className="bento-cloud-section-tags">
        {v.cloudPractices.map((item) => (
          <span key={item} className="bento-cloud-tag" style={{ borderColor: '#f59e0b33', background: '#f59e0b15', color: '#f59e0b' }}>{item}</span>
        ))}
      </div>
    </div>
  );
}

function ContactCell({ lang }: { lang: Lang }) {
  const t = content[lang].contact;
  const [copied, setCopied] = useState<CopiedField>(null);

  const copy = useCallback((text: string, field: CopiedField) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  }, []);

  return (
    <div className="bento-contact-cell">
      <span className="bento-contact-label">
        {lang === 'en' ? 'CONTACT' : 'CONTACTO'}
      </span>
      <button
        className="bento-contact-row"
        onClick={() => copy(t.email, 'email')}
      >
        <Mail size={16} />
        <span className="bento-contact-value">{t.email}</span>
        <span className="bento-contact-copy">
          {copied === 'email'
            ? (lang === 'en' ? 'Copied!' : 'Copiado!')
            : (lang === 'en' ? 'Copy' : 'Copiar')}
        </span>
      </button>
      <button
        className="bento-contact-row"
        onClick={() => copy(t.phone, 'phone')}
      >
        <Phone size={16} />
        <span className="bento-contact-value">{t.phone}</span>
        <span className="bento-contact-copy">
          {copied === 'phone'
            ? (lang === 'en' ? 'Copied!' : 'Copiado!')
            : (lang === 'en' ? 'Copy' : 'Copiar')}
        </span>
      </button>
      <div className="bento-contact-row" style={{ cursor: 'default' }}>
        <svg viewBox="0 0 127.14 96.36" width="16" height="16" fill="currentColor"><path d="M107.7 8.07A105.15 105.15 0 0 0 81.47 0a72.06 72.06 0 0 0-3.36 6.83 97.68 97.68 0 0 0-29.11 0A72.37 72.37 0 0 0 45.64 0a105.89 105.89 0 0 0-26.25 8.09C2.79 32.65-1.71 56.6.54 80.21a105.73 105.73 0 0 0 32.17 16.15 77.7 77.7 0 0 0 6.89-11.11 68.42 68.42 0 0 1-10.85-5.18c.91-.66 1.8-1.34 2.66-2.04a75.57 75.57 0 0 0 64.32 0c.87.71 1.76 1.39 2.66 2.04a68.68 68.68 0 0 1-10.87 5.19 77 77 0 0 0 6.89 11.1 105.25 105.25 0 0 0 32.19-16.14c2.64-27.38-4.51-51.11-18.9-72.15zM42.45 65.69C36.18 65.69 31 60 31 53.05s5-12.68 11.45-12.68S54 46.06 53.89 53.05c0 6.95-5.11 12.64-11.44 12.64zm42.24 0C78.41 65.69 73.25 60 73.25 53.05s5-12.68 11.44-12.68S96.23 46.06 96.12 53.05c0 6.95-5.08 12.64-11.43 12.64z"/></svg>
        <span className="bento-contact-value">JordanRO2</span>
        <span className="bento-contact-copy" style={{ opacity: 0.4 }}>Discord</span>
      </div>
    </div>
  );
}

function SocialCell({
  type,
  url,
  label,
}: {
  type: 'github' | 'linkedin';
  url: string;
  label: string;
}) {
  const icon = type === 'github' ? (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0 1 12 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" />
    </svg>
  ) : (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`bento-social-cell bento-social-${type}`}
    >
      <span className="bento-social-icon">{icon}</span>
      <span className="bento-social-label">{label}</span>
      <ExternalLink size={14} className="bento-social-arrow" />
    </a>
            
  );
}

function EducationCell({ lang }: { lang: Lang }) {
  const t = content[lang].education;
  return (
    <div className="bento-edu-cell">
      <div className="bento-edu-header">
        <GraduationCap size={18} style={{ opacity: 0.6 }} />
        <span className="bento-edu-label">
          {lang === 'en' ? 'EDUCATION' : 'EDUCACION'}
        </span>
      </div>
      {t.items.map((item, i) => (
        <div key={i} className="bento-edu-item" style={i > 0 ? { marginTop: '.8rem', paddingTop: '.8rem', borderTop: '1px solid rgba(255,255,255,0.06)' } : undefined}>
          <h3 className="bento-edu-degree">{item.degree}</h3>
          <p className="bento-edu-school">{item.school}</p>
          <span className="bento-edu-period">{item.period}</span>
        </div>
      ))}
    </div>
  );
}

function AboutCell({ lang, variant }: { lang: Lang; variant: Variant }) {
  const lines = variantContent[lang][variant].aboutLines;

  return (
    <div className="bento-about-cell">
      <div className="bento-about-header">
        
        <span className="bento-about-label">
          {lang === 'en' ? 'ABOUT' : 'SOBRE MI'}
        </span>
      </div>
      {lines.map((line, i) => (
        <p key={i} className="bento-about-line">{line}</p>
      ))}
    </div>
  );
}

function SkeletonGrid() {
  const cells = [
    // Row 1: photo + titles + stats
    { col: 3, row: 2, h: 280 },
    { col: 5, row: 1, h: 130 },
    { col: 4, row: 1, h: 130 },
    // Row 2: about + status + location
    { col: 5, row: 1, h: 120 },
    { col: 2, row: 1, h: 120 },
    { col: 2, row: 1, h: 120 },
    // Row 3: job1(5) + job2(4) + job3(3)
    { col: 5, row: 1, h: 180 },
    { col: 4, row: 1, h: 180 },
    { col: 3, row: 1, h: 180 },
    // Row 4: job4(3) + job5(3) + skills-hl(6)
    { col: 3, row: 1, h: 160 },
    { col: 3, row: 1, h: 160 },
    { col: 6, row: 1, h: 160 },
    // Row 5: 3 skill bentos (4+4+4)
    { col: 4, row: 1, h: 200 },
    { col: 4, row: 1, h: 200 },
    { col: 4, row: 1, h: 200 },
    // Row 6: project1(5) + project2(4) + project3(3)
    { col: 5, row: 1, h: 160 },
    { col: 4, row: 1, h: 160 },
    { col: 3, row: 1, h: 160 },
    // Row 7: project4(3) + education(5) + contact(4)
    { col: 3, row: 1, h: 150 },
    { col: 5, row: 1, h: 150 },
    { col: 4, row: 1, h: 150 },
    // Row 8: github(4) + linkedin(4) + hackerone(4)
    { col: 4, row: 1, h: 70 },
    { col: 4, row: 1, h: 70 },
    { col: 4, row: 1, h: 70 },
  ];

  return (
    <div className="bento-grid bento-skeleton-grid">
      {cells.map((c, i) => (
        <SkeletonLoader
          key={i}
          style={{
            gridColumn: `span ${c.col}`,
            gridRow: `span ${c.row}`,
            minHeight: c.h ? `${c.h}px` : undefined,
            animationDelay: `${i * 0.06}s`,
          }}
        />
      ))}
    </div>
  );
}

export function BentoPortfolio({ lang, variant }: { lang: Lang; variant: Variant }) {
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, -40]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
      setTimeout(() => setLoaded(true), 100);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const t = content[lang];
  const d = (n: number) => n * 0.06;

  return (
    <div className="bento-container" ref={containerRef}>
      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div
            key="skeleton"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <SkeletonGrid />
          </motion.div>
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            <motion.div className="bento-grid" style={{ y: parallaxY }}>
              {/* Row 1: HERO - [PHOTO 3col 2row] [TITLES 5col] [STATS 4col] */}
              <BentoCell colSpan={3} rowSpan={2} delay={d(0)} glowColor="rgba(59,130,246,0.12)" className="cell-photo">
                <PhotoCell lang={lang} />
              </BentoCell>
              <BentoCell colSpan={5} rowSpan={1} delay={d(1)} glowColor="rgba(139,92,246,0.12)" className="cell-titles">
                <TitlesCell lang={lang} variant={variant} />
              </BentoCell>
              <BentoCell colSpan={4} rowSpan={1} delay={d(2)} glowColor="rgba(6,182,212,0.12)" className="cell-stats">
                <StatsCell lang={lang} loaded={true} />
              </BentoCell>

              {/* Row 2: [ABOUT 5col] [STATUS 2col] [LOCATION 2col] */}
              <BentoCell colSpan={5} rowSpan={1} delay={d(3)} glowColor="rgba(139,92,246,0.1)" className="cell-about">
                <AboutCell lang={lang} variant={variant} />
              </BentoCell>
              <BentoCell colSpan={2} rowSpan={1} delay={d(4)} glowColor="rgba(34,197,94,0.15)" className="cell-status">
                <StatusCell lang={lang} />
              </BentoCell>
              <BentoCell colSpan={2} rowSpan={1} delay={d(5)} glowColor="rgba(59,130,246,0.1)" className="cell-location">
                <LocationCell lang={lang} />
              </BentoCell>

              {/* Row 3: EXPERIENCE - [JOB1 5col] [JOB2 4col] [JOB3 3col] */}
              <BentoCell colSpan={5} rowSpan={1} delay={d(6)} glowColor="rgba(59,130,246,0.1)" className="cell-job cell-job-current">
                <JobCell lang={lang} jobIndex={0} isCurrent={true} />
              </BentoCell>
              <BentoCell colSpan={4} rowSpan={1} delay={d(7)} glowColor="rgba(139,92,246,0.08)" className="cell-job">
                <JobCell lang={lang} jobIndex={1} isCurrent={false} />
              </BentoCell>
              <BentoCell colSpan={3} rowSpan={1} delay={d(8)} glowColor="rgba(6,182,212,0.08)" className="cell-job">
                <JobCell lang={lang} jobIndex={2} isCurrent={false} />
              </BentoCell>

              {/* Row 4: [JOB4 3col] [JOB5 3col] [SKILL-HIGHLIGHT 6col] */}
              <BentoCell colSpan={3} rowSpan={1} delay={d(9)} glowColor="rgba(249,115,22,0.08)" className="cell-job">
                <JobCell lang={lang} jobIndex={3} isCurrent={false} />
              </BentoCell>
              <BentoCell colSpan={3} rowSpan={1} delay={d(10)} glowColor="rgba(34,197,94,0.08)" className="cell-job">
                <JobCell lang={lang} jobIndex={4} isCurrent={false} />
              </BentoCell>
              <BentoCell colSpan={6} rowSpan={1} delay={d(11)} glowColor="rgba(139,92,246,0.1)" className="cell-skill-hl">
                <SkillHighlightCell lang={lang} variant={variant} />
              </BentoCell>

              {/* Row 5: SKILLS - 3 separate bentos */}
              <BentoCell colSpan={4} rowSpan={1} delay={d(12)} glowColor="rgba(59,130,246,0.08)" className="cell-skills-tech">
                <SkillsTechCell lang={lang} variant={variant} />
              </BentoCell>
              <BentoCell colSpan={4} rowSpan={1} delay={d(12.5)} glowColor="rgba(34,197,94,0.08)" className="cell-skills-tools">
                <SkillsToolsCell lang={lang} variant={variant} />
              </BentoCell>
              <BentoCell colSpan={4} rowSpan={1} delay={d(13)} glowColor="rgba(245,158,11,0.08)" className="cell-skills-practices">
                <SkillsPracticesCell lang={lang} variant={variant} />
              </BentoCell>

              {/* Row 6: PROJECTS - [P1 4col] [P2 4col] [P3 4col] */}
              <BentoCell colSpan={5} rowSpan={1} delay={d(13)} glowColor="rgba(59,130,246,0.15)" className="cell-project" style={{ background: PROJECT_GRADIENTS[0] }}>
                <ProjectCell lang={lang} variant={variant} index={0} featured />
              </BentoCell>
              <BentoCell colSpan={4} rowSpan={1} delay={d(14)} glowColor="rgba(34,197,94,0.15)" className="cell-project" style={{ background: PROJECT_GRADIENTS[1] }}>
                <ProjectCell lang={lang} variant={variant} index={1} />
              </BentoCell>
              <BentoCell colSpan={3} rowSpan={1} delay={d(15)} glowColor="rgba(239,68,68,0.15)" className="cell-project" style={{ background: PROJECT_GRADIENTS[2] }}>
                <ProjectCell lang={lang} variant={variant} index={2} />
              </BentoCell>

              {/* Row 7: [P4 4col] [EDUCATION 4col] [CONTACT 4col] */}
              <BentoCell colSpan={3} rowSpan={1} delay={d(16)} glowColor="rgba(139,92,246,0.15)" className="cell-project" style={{ background: PROJECT_GRADIENTS[3] }}>
                <ProjectCell lang={lang} variant={variant} index={3} />
              </BentoCell>
              <BentoCell colSpan={5} rowSpan={1} delay={d(17)} glowColor="rgba(139,92,246,0.08)" className="cell-edu">
                <EducationCell lang={lang} />
              </BentoCell>
              <BentoCell colSpan={4} rowSpan={1} delay={d(18)} glowColor="rgba(249,115,22,0.1)" className="cell-contact">
                <ContactCell lang={lang} />
              </BentoCell>

              {/* Row 8: FOOTER - [GITHUB 6col] [LINKEDIN 6col] */}
              <BentoCell colSpan={4} rowSpan={1} delay={d(19)} glowColor="rgba(255,255,255,0.06)" className="cell-social">
                <a href="https://github.com/JordanRO2" target="_blank" rel="noopener noreferrer" className="bento-social-link">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/></svg>
                  <span>GitHub</span>
                  <ExternalLink size={14} style={{opacity:0.5}} />
                </a>
              </BentoCell>
              <BentoCell colSpan={4} rowSpan={1} delay={d(20)} glowColor="rgba(59,130,246,0.08)" className="cell-social">
                <a href="https://linkedin.com/in/jordanro2" target="_blank" rel="noopener noreferrer" className="bento-social-link">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  <span>LinkedIn</span>
                  <ExternalLink size={14} style={{opacity:0.5}} />
                </a>
              </BentoCell>
              <BentoCell colSpan={4} rowSpan={1} delay={d(21)} glowColor="rgba(239,68,68,0.08)" className="cell-social cell-hackerone">
                <a href="https://hackerone.com/jordanro2" target="_blank" rel="noopener noreferrer" className="bento-social-link">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M7.207 0c-.4836 0-.8774.1018-1.1823.3002-.3044.2003-.4592.4627-.4592.7798v21.809c0 .2766.1581.5277.4752.7609.315.2335.7031.3501 1.1664.3501.4427 0 .8306-.1166 1.1678-.3501.3352-.231.5058-.4843.5058-.761V1.0815c0-.319-.1623-.5769-.4893-.7813C8.0644.1018 7.6702 0 7.207 0zm9.5234 8.662c-.4836 0-.8717.0981-1.1683.3007l-4.439 2.7822c-.1988.1861-.2841.4687-.2473.855.0342.3826.2108.747.5238 1.0907.3145.346.6662.5626 1.0684.6547.3963.0899.6973.041.8962-.143l1.7551-1.0951v9.7817c0 .2767.1522.5278.4607.761.3007.2335.6873.3501 1.1504.3501.463 0 .863-.1166 1.1983-.3501.3371-.2332.5058-.4843.5058-.761V9.7381c0-.3193-.165-.577-.4898-.7754-.3252-.2026-.7288-.3007-1.2143-.3007z"/></svg>
                  <span>HackerOne</span>
                  
                  <ExternalLink size={14} style={{opacity:0.5}} />
                </a>
              </BentoCell>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
